import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { PortalService } from '../../../../../../services/portal.service';
import { EnrollmentFormService } from '../../../../../../services/EnrollmentFormService';
import { FormGroup } from '@angular/forms';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { EnrollmentComponent } from '../enrollment/enrollment.component';
import { TelemetryService } from '../../../../../../services/telemetry.service';

@Component({
  selector: 'app-review-info',
  templateUrl: './review-info.component.html',
  styleUrls: ['./review-info.component.scss'],
  providers: [DatePipe, TitleCasePipe],
})
export class ReviewInfoComponent implements OnInit {
  enrollmentDetails: any = [];
  isLoading: boolean = true;
  reviewForm: FormGroup<any>;
  enrollmentId: any;
  currentSequenceStep: string;
  states: any = [];
  editFlow = false;

  constructor(private efs: EnrollmentFormService,
    private enrollmentComponent: EnrollmentComponent,
    private router: Router,
    private route: ActivatedRoute,
    private portalService: PortalService,
    private datePipe: DatePipe,
    private titleCasePipe: TitleCasePipe,
    private telemetryService: TelemetryService) {
    this.reviewForm = this.efs.enrollmentForm as FormGroup;
  }

  getPhoneTypes(typeId: number) {
    switch (typeId) {
      case 1:
        return "Home";
      case 2:
        return "Work"
      default:
        return "";
    }
  };

  getFullAddress(line1: string, line2: string, city: string, stateCode: string, zipCode: string) {
    let address = "";
    if (line1) address += line1;
    if (line2) address += ", " + line2;
    if (city) address += ", " + city;
    if (stateCode) address += ", " + this.getStateFromCode(stateCode);
    if (zipCode) address += ", " + zipCode;
    return address;
  }

  ngOnInit(): void {
    this.currentSequenceStep = localStorage.getItem('CurrentEnrollmentStep');
    const eid = this.reviewForm.get('enrollmentId')?.value;
    this.portalService.getStatesList().subscribe({
      next: (data) => {
        this.states = data;
      },
      error: (err) => {
        console.error('Error loading states:', err);
      }
    });
    this.efs.enrollmentID$.subscribe((id) => {
      this.enrollmentId = id;
      console.log('Received Enrollment ID:', id);
    });
    this.route.paramMap.subscribe(params => {
      if (params.get('id')) {
        this.enrollmentId = params.get('id');
        this.editFlow = true;
      }
    });
    this.portalService.getEnrollmentById(null, this.enrollmentId).subscribe((res) => {
      console.log(res);
      this.efs.patchEnrollmentForm(res);
      this.isLoading = false;
      let insurancedoc: any = {};
      let incomedoc: any = {};

      if (res?.enrollmentAttachmentViewDTOs?.length) {
        insurancedoc = res?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 7);
        if (insurancedoc) {
          insurancedoc.name = insurancedoc.filePath.split('/').pop();
          insurancedoc.type = insurancedoc.filePath.split('.').pop();
        }
        incomedoc = res?.enrollmentAttachmentViewDTOs.find((attachment: any) => attachment.enrollmentAttachmentTypeId === 5);
        if (incomedoc) {
          incomedoc.name = incomedoc.filePath.split('/').pop();
          incomedoc.type = incomedoc.filePath.split('.').pop();
        }
      }

      console.log(insurancedoc, incomedoc);
      const insurance = res?.enrollmentInsuranceViewDTOs.find((insurance: any) => insurance.insuranceType.toLowerCase() === 'other')
      this.enrollmentDetails = [
        {
          title: 'Personal Information',
          editPath: `/enrollment/${this.enrollmentId}/Profile`,
          items: [
            { detailName: 'Legal First Name', detailValue: res?.firstName },
            { detailName: 'Legal Last Name', detailValue: res?.lastName },
            { detailName: 'Date of Birth', detailValue: this.datePipe.transform(res?.birthdate, 'MM/dd/yyyy') },
            { detailName: 'Email Address', detailValue: res?.email },
            { detailName: 'Primary Phone Type', detailValue: this.getPhoneTypes(res.primaryPhoneTypeId) },
            { detailName: 'Primary Phone Number', detailValue: res?.phone },
            { detailName: 'Alternate Phone Type', detailValue: this.getPhoneTypes(res.alternatePhoneTypeId) },
            { detailName: 'Alternate Phone Number', detailValue: res?.altPhone },
            { detailName: 'Home Address', detailValue: this.getFullAddress(res?.billingAddress1, res?.billingAddress2, res?.billingCity, res?.billingState, res?.billingZip) },
            { detailName: 'Shipping Address', detailValue: this.getFullAddress(res?.shippingAddress1, res?.shippingAddress2, res?.shippingCity, res?.shippingState, res?.shippingZip) },
            { detailName: 'US Resident', detailValue: this.titleCasePipe.transform(res?.usCitizen) },
          ],
        },
        {
          title: 'Insurance Information',
          editPath: `/enrollment/${this.enrollmentId}/Insurance`,
          items: [
            { detailName: 'Do you have insurance or other prescription drug coverage?', detailValue: res?.enrollmentInsuranceViewDTOs.length > 0 ? 'Yes' : 'No' },
            {
              detailName: 'Insurance Type',
              detailValue: res?.enrollmentInsuranceViewDTOs.sort((a: any, b: any) => a.insuranceTypeID - b.insuranceTypeID).map((insurance: any) => insurance.insuranceType).join(', '),
              hidden: res?.enrollmentInsuranceViewDTOs.length === 0
            },
            { detailName: 'Plan Name', detailValue: insurance?.insuranceCompanyName, hidden: !insurance },
            { detailName: 'Member ID', detailValue: insurance?.insuranceInsuredID, hidden: !insurance },
            {
              detailName: 'Proof of Insurance', detailValue: this.titleCasePipe.transform(insurancedoc?.name),
              customTemplate: true,
              hidden: res?.enrollmentInsuranceViewDTOs.length === 0
            },
          ],
        },
        {
          title: 'Physician Information',
          editPath: `/enrollment/${this.enrollmentId}/Physician`,
          items: [
            { detailName: 'First Name', detailValue: res?.physicianFirstName },
            { detailName: 'Last Name', detailValue: res?.physicianLastName },
            { detailName: 'Phone', detailValue: res?.physicianPhone },
            { detailName: 'Email', detailValue: res?.physicianEmail },
            { detailName: 'Fax', detailValue: res?.physicianFax },
            { detailName: 'Address', detailValue: res?.physicianAddress1 + " " + res?.physicianAddress2 },
            { detailName: 'City', detailValue: res?.physicianCity },
            { detailName: 'State', detailValue: this.getStateFromCode(res?.physicianState) },
            { detailName: 'Zip code', detailValue: res?.physicianZip },
          ],
        },
        {
          title: 'Income Information',
          editPath: `/enrollment/${this.enrollmentId}/Income`,
          items: [
            // { detailName: 'Household Income', detailValue: res?.enrollmentEligibilityViewDTOs?.income },
            { detailName: 'Household Income', detailValue: res?.enrollmentEligibilityViewDTOs?.income ?? 0 },
            { detailName: 'Household Size', detailValue: res?.enrollmentEligibilityViewDTOs?.household },
            { detailName: 'Proof of Income', detailValue: this.titleCasePipe.transform(incomedoc?.name), customTemplate: true },
          ],
        },
      ];
    });
  }

  get nextStep() {
    return this.efs.getNextStep();
  }

  getStateFromCode(code: string) {
    return this.states.length && this.states.find((state: any) => state.stateProvCode === code)?.stateProvName || '';
  }

  onSave(isSaveAndExit = false): void {
    if(!isSaveAndExit) {
			this.telemetryService.logEvent('Next Button Clicked', {
				step: this.nextStep.enrollmentStepName,
				timestamp: new Date().toISOString(),
			});
		}

    this.reviewForm.patchValue({
      lastEnrollmentStep: this.currentSequenceStep
    });
    this.isLoading = true;
    this.portalService.createEnrollment(this.reviewForm.value, this.enrollmentId).subscribe((value: any) => {
      this.isLoading = false;
      if (isSaveAndExit) {
        this.efs.resetForm();
        this.router.navigate(['/submissions'], { relativeTo: this.route });
      } else {
        if (this.editFlow) {
          this.router.navigate([`../${this.nextStep.enrollmentRoute}`], { relativeTo: this.route });
          this.enrollmentComponent.setCurrentStep(this.nextStep.enrollmentStepName, 'true');
        } else {
          this.enrollmentComponent.setCurrentStep(this.nextStep.enrollmentStepName);
        }
      }
    });
  }
}
